import { useEffect, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useUserInit } from '@/contexts/UserInitContext'
import {
  buildSubscriptionCheckoutPath,
  isSubscriptionCheckoutTier,
} from '@/utils/subscriptionCheckoutIntent'

const STORAGE_KEY = 'superschema_pending_checkout'

/**
 * Hook to redirect users with a pending tier checkout to /pricing after signup.
 *
 * Flow:
 * 1. User clicks "Get Started" on pricing page (Astro or React)
 * 2. Tier selection is saved to sessionStorage as 'superschema_pending_checkout'
 * 3. User authenticates via Clerk (email or OAuth)
 * 4. Clerk redirects to /dashboard (not back to pricing)
 * 5. UserInitProvider creates the user record in the database
 * 6. This hook detects legacy pending checkout data and redirects to
 *    /checkout/subscription instead of reopening a pricing modal.
 *
 * Usage: Add to main App component that renders after authentication
 */
export function usePendingCheckoutRedirect() {
  const { isLoaded, isSignedIn } = useAuth()
  const { isInitialized } = useUserInit()
  const navigate = useNavigate()
  const location = useLocation()
  const [handled, setHandled] = useState(false)

  useEffect(() => {
    // Only run once user is authenticated AND initialized in database
    if (!isLoaded || !isSignedIn || !isInitialized) return

    // Only run once per session
    if (handled) return

    // Don't redirect if user is already in the dedicated checkout flow
    if (location.pathname === '/checkout/subscription') return

    // Check for pending checkout in sessionStorage
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return

    // Validate it's parseable
    try {
      const parsed = JSON.parse(raw) as { tier?: string; isAnnual?: boolean }
      if (!isSubscriptionCheckoutTier(parsed.tier)) {
        sessionStorage.removeItem(STORAGE_KEY)
        return
      }

      console.log('🔄 [usePendingCheckoutRedirect] Found legacy pending checkout, redirecting to dedicated checkout...')
      setHandled(true)
      navigate(buildSubscriptionCheckoutPath({
        tier: parsed.tier,
        billing: parsed.isAnnual ? 'year' : 'month',
      }), { replace: true })
    } catch {
      sessionStorage.removeItem(STORAGE_KEY)
      return
    }
  }, [isLoaded, isSignedIn, isInitialized, location.pathname, handled, navigate])
}
